import { Component } from 'preact';
import type { Decimal, Id, Maybe } from '../../common/data.js';
import {
	ComputedProduct,
	FlowDecorationType,
	FullPublicProduct,
	Product,
	ProductImage,
	sizeGroups,
} from '../../common/data/products.js';
import { ProductTemplate, Template, Upload, ValidPreviewWidth } from '../../common/data/designs.js';
import { ContentBlock, ContentContext } from './content.js';
import { ContentExtras } from '../../common/data/content.js';
import { Picture } from './image.js';
import {
	apply,
	capitalize,
	decimal,
	edit,
	ensure,
	formatPrice,
	indexInto,
	isEmpty,
	isEmptyOrNone,
	isIn,
	isNone,
	isSome,
	logRejection,
	makeIndex,
	makeSlug,
	maybe,
	notEmpty,
	num,
	objectEntries,
	objectKeys,
	queue,
	range,
	tuple,
} from '../../common/util.js';
import { Icon, StarBar } from './icons.js';
import { getProductPreviewImageUrl, getUploadPreviewURL, makeDesignPreviewUrl } from '../lib/images.js';
import { addDesignPackageToCart, calculateUploadColors, uploadArtwork } from '../lib/api.js';
import { Popup, showAlert } from './popup.js';
import { classNames, getEventChecked, submitQueue } from '../lib/util.js';
import { Input } from './input.js';
import { SizeChartTable } from './size-chart-table.js';
import { ProductSlider } from './product-slider.js';
import { Context } from '../../common/context.js';
import { ColorTuple, mainColorGroups, rgbToHex } from '../../common/data/colors.js';
import { OrderItemFeedbackEntry, OrderItemPhoto } from '../../common/data/orders.js';
import { formatTimestamp } from '../../common/data/dates.js';
import { orderRatingCriteria } from '../../common/data/feedback.js';
import { SiteContext } from '../context.js';
import { StoreAutofillKit } from '../../common/data/stores.js';
import { lazy, Suspense } from 'preact/compat';
import { Loading } from './loading.js';

const ColorPickerImpl = lazy(() => import('./colorpicker.js').then(m => m.ColorPicker));

export interface SingleProductDecAlternative {
	decoration: FlowDecorationType,
	alias: string
}

export interface SingleProductPricing {
	quantities: number[];
	rows: {
		label: Maybe<string>;
		values: Maybe<Decimal>[];
	}[];
	stitchCount?: Maybe<number>;
	logoFee?: Maybe<Decimal>;
}

interface SingleProductPageState {
	selectedImage: Maybe<ProductImage>;
	selectedProductTemplate?: Maybe<ProductTemplate>;
	dialog?: string;
	uploads: Upload[];
	selectedProducts: Record<Id<Product>, boolean>;
	selectedTemplates: Record<Id<Template>, boolean>;
	selectedKits: Record<Id<StoreAutofillKit>, boolean>;
	comment?: string;
	photoPopup?: Maybe<{
		photo: OrderItemPhoto;
		review?: Maybe<OrderItemFeedbackEntry>;
	}>;
	colorList?: Maybe<Maybe<ColorTuple>[]>;
	stockColorList?: Maybe<Maybe<ColorTuple>[]>;
}

export interface SingleProductPageProps {
	context: Context;
	extras: ContentExtras;
	productTemplates: ProductTemplate[];
	product: FullPublicProduct;
	images: ProductImage[];
	sanmarProductId: Maybe<string>;
	productSizes: string[];
	templatesAreColors?: boolean;
	isDpkg: boolean;
	productsIncluded: number | 'all';
	products: ComputedProduct[];
	selectedProductTemplateId?: Maybe<string>;
	reviews: OrderItemFeedbackEntry[];
	reviewAverage: Maybe<Decimal>;
	reviewCount: number;
	decoration: Maybe<FlowDecorationType>;
	pricing: Maybe<SingleProductPricing>;
	decAlternatives: Maybe<SingleProductDecAlternative[]>;
	allowedKits: Maybe<StoreAutofillKit[]>;
	ownUploads: Maybe<Upload[]>;
}

export const getGetSingleProductTemplateUrl = (pt: ProductTemplate) => {
	if (pt.template != 'base00') return pt.template;
	if (pt.sanmar_color_name) return makeSlug(pt.sanmar_color_name);
	return pt.id;
};

const decTypeDescr: Record<FlowDecorationType, string> = {
	'cut-and-sew': 'All Over Print',
	'screen-print': 'Screen Print',
	'sub-blanks': 'Dye Sublimation',
	dtf: 'Direct-to-Film',
	embroidery: 'Embroidery',
};
const sanmarImageDesc: Record<string, string> = {
	frontFlat: 'Front product photo',
	backFlat: 'Back product photo',
	frontModel: 'Front model photo',
	threeQModel: 'Model photo',
	sideModel: 'Side model photo',
	backModel: 'Back model photo',
};

const trueKeys = <T extends keyof any> (rec: Record<T, boolean>) => objectEntries(rec).filter(e => e[1]).map(e => e[0]);


export class SingleProductPage extends Component<SingleProductPageProps, SingleProductPageState> {

	static contextType = SiteContext;
	declare context: SiteContext;
	
	constructor(props: SingleProductPageProps) {
		super(props);
		const { product, products, productsIncluded, selectedProductTemplateId, allowedKits } = props;
		const selectedProductTemplate = maybe(selectedProductTemplateId, ptId => props.productTemplates.find(pt => pt.id == ptId));
		const byKit = product.features?.designPackage?.selectProductsBy == 'kit' && !!allowedKits;
		const selectedProducts = (productsIncluded && (productsIncluded == 'all' || productsIncluded >= products.length) && !byKit) ? Object.fromEntries(products.map(p => [p.id, true])) : {};
		const dpkgFeatures = product.features?.designPackage;
		this.state = {
			uploads: [],
			selectedImage: (selectedProductTemplate && props.images.find(pi => pi.template_preview || pi.sanmar_preview)) || props.images[0],
			selectedProductTemplate,
			colorList: maybe(dpkgFeatures?.designColors, c => c ? range(c).map(_ => null) : null),
			stockColorList: maybe(dpkgFeatures?.stockColors, c => c ? range(c).map(_ => [127, 127, 127]) : null),
			selectedProducts,
			selectedTemplates: {},
			selectedKits: byKit && maybe(allowedKits, ak => Object.fromEntries(ak.map(k => [k.id, true]))) || {},
		};
	}
	
	componentDidMount() {
	}
	
	render(props: SingleProductPageProps, state: SingleProductPageState) {
		
		const {
			context,
			extras,
			productsIncluded,
			product,
			images,
			productTemplates,
			sanmarProductId,
			productSizes,
			products,
			isDpkg,
			templatesAreColors,
			reviews,
			reviewCount,
			reviewAverage,
			decoration,
			pricing,
			decAlternatives,
			allowedKits,
			ownUploads,
		} = props;
		const { domain } = context;
		const {
			comment,
			dialog,
			selectedImage,
			selectedProductTemplate,
			selectedProducts,
			selectedKits,
			selectedTemplates,
			uploads,
			photoPopup,
			colorList,
			stockColorList,
		} = state;
		
		const { brand_name } = domain;
		const { information, alias, features, info_url, overview, name, description, id, size_chart, available, discontinued } = product;
		const dpkgFeatures = product.features?.designPackage;
		
		const ssr = this.context.isServerSide.peek();
		
		const getProductsText = () => {
			if (allProducts) return <p class="single-product__products-text">This design package includes design services for all of the following products or design assets. You can deselect any that you'd like to opt out from.</p>;
			if (!productsIncluded) return <p class="single-product__products-text">This design package does not include product designs.</p>;
			return <p class="single-product__products-text">Choose {productsIncluded > 1
				? `up to ${productsIncluded} products`
				: 'the product'} you would like us to design for you.</p>;
		};
		
		const allProducts = productsIncluded == 'all';
		const productsSelected = trueKeys(selectedProducts).length;
		const maxSelected = allProducts ? false : productsSelected >= productsIncluded;
		
		const selectKits = !!allowedKits && features?.designPackage?.selectProductsBy == 'kit';
		const kitsSelected = trueKeys(selectedKits).length;
		
		const anyColorsSelected = colorList?.some(isSome);
		
		const getImageUrl = (pi: ProductImage, previewWidth: ValidPreviewWidth, i = 0, side = 'front') => {
			const pt = selectedProductTemplate ?? productTemplates[i % productTemplates.length];
			if (pi.template_preview) return pt && makeDesignPreviewUrl(
				null,
				{ product: id, productTemplate: pt.id, previewWidth, side },
			);
			if (pi.sanmar_preview && pt) return `/data/product-color-images/${sanmarProductId}/${pt.sanmar_color_name?.replace(
				/\W+/g,
				'',
			)}.${pi.sanmar_preview}.${previewWidth}.jpg`;
			return getProductPreviewImageUrl(pi, previewWidth);
		};
		
		const getImageAlt = (pi: ProductImage) => (
			pi.caption
			?? maybe(pi.sanmar_preview, pp => sanmarImageDesc[pp])
			?? (
				pi.main ? 'Product preview image'
				: pi.template_preview ? 'Rendered design template preview'
				: pi.design ? 'Rendered product preview'
				: 'Product preview'
			)
		);
		
		const splitSizes = productSizes.map(s => s.split('-'));
		const groups = [...new Set(splitSizes.map(s => s[0]!))];
		const groupedSizes = groups.map(gid => ({
			gid,
			name: sizeGroups[gid] || gid,
			sizes: splitSizes.filter(s => s[0] == gid && !!s[1]).map(s => s.slice(1).join('-')),
		}));
		const canStack = maybe(
			features?.sizes,
			s => s.length > 2,
		) && !!features?.roster && !!features.nameNumber;
		const bigSide = canStack ? 'stack' : 'front';
		
		const criteria = Object.entries(orderRatingCriteria);
		
		const productMap = makeIndex(products);
		const productCheckbox = (p: ComputedProduct) => <label
			className={classNames(
				'single-product__item',
				!!selectedProducts[p.id] && '--selected',
				(maxSelected && !selectedProducts[p.id]) && '--disabled',
			)}
		>
			<input
				type="checkbox"
				checked={!!selectedProducts[p.id]}
				disabled={(maxSelected && !selectedProducts[p.id]) || ssr}
				onChange={e => this.toggleSelection('products', [p.id], getEventChecked(e))}
			/>
			<span className="single-product__item-info">{p.name}</span>
			{maybe(p.preview_image, img => maybe(getProductPreviewImageUrl(img), src => <Picture
				src={src}
				aria-hidden={true}
				loading="lazy"
				style={{ aspectRatio: p.image_size?.join('/') }}
			/>))}
		</label>;
		
		const templateSelection = isDpkg && notEmpty(productTemplates) && features?.designPackage?.templateSelection || null;
		const maxTemplates = templateSelection?.max ?? 1;
		const multiTemplates = maxTemplates > 1;
		const templatesSelected = Object.values(this.state.selectedTemplates).filter(v => v).length;
		const maxTemplatesSelected = templatesSelected >= maxTemplates;
		const templateColors = templateSelection && anyColorsSelected ? Object.fromEntries(
			colorList!.map((val, i) => [mainColorGroups[i], val]).filter(([_cid, val]) => isSome(val))
		) : null;
		
		return <main class="single-product">
			
			<section className="single-product__header">
				<div className="single-product__description">
					<h1 className="single-product__title">{name}</h1>
					{isSome(reviewAverage) && <div className="single-product__rating">
						<div className="single-product__rating-stars" title={`${num(reviewAverage)} stars`}>
							<StarBar rating={num(reviewAverage)} />
						</div>
						<span className="single-product__rating-review">({reviewCount} {reviewCount == 1 ? 'review' : 'reviews'})</span>
						<div className="single-product__bar"></div>
						<a href="#reviews" className="single-product__link">Read Reviews</a>
					</div>}
					{description && <ContentContext.Provider value={{
						target: ['web'],
						domain,
						website: `https://${domain.host}`,
						extras,
						link: s => s,
						image: s => s,
					}}>
						<ContentBlock item={description} />
					</ContentContext.Provider>}
				</div>
				{notEmpty(images) && <div className="single-product__images">
					{selectedImage && <div className="single-product__image">
						{maybe(getImageUrl(selectedImage, 512, images.indexOf(selectedImage), bigSide), src => <Picture src={src} alt={getImageAlt(selectedImage)}/>)}
					</div>}
					{images.length > 1 && <div className="single-product__thumbnail-list">
						{images.map((pi, i) => maybe(getImageUrl(pi, 128, i), src => <a
							className="single-product__thumbnail-list__item"
							href={getImageUrl(pi, 1024, i) ?? '#'}
							onClick={e => {
								this.setState({ selectedImage: pi });
								e.preventDefault();
							}}
						>
							<Picture src={src} alt={getImageAlt(pi)}/>
						</a>))}
					</div>}
				</div>}
				<div className="single-product__details">
					{!available ? <p className="single-product__unavailable">
						This product is {discontinued ? ' no longer available' : 'currently not available'}.
					</p> : discontinued ? <p className="single-product__unavailable">
						This product has been discontinued and is only available to order while stocks last.
					</p> : ''}
					{!isDpkg && <>
						{available && <a className="single-product__button" href={
							selectedProductTemplate
								? `/builder?product=${alias}&template=${getGetSingleProductTemplateUrl(selectedProductTemplate)}`
								: `/builder?product=${alias}`
						}>Start Designing</a>}
						{templatesAreColors && <>
							{/*<div className="single-product__colors__title">
								<span> Colors: </span>
								<div className="single-product__colors__title__toggle-btn">
									<span> Ordering fewer than 6 </span>
									<label className="switch">
										<input type="checkbox"/>
										<span className="slider round"></span>
									</label>
								</div>
							</div>*/}
							<div className="single-product__colors">
								{productTemplates.map((des, idx) => <a
									className="single-product__color"
									onClick={e => { this.setProductTemplate(des); e.preventDefault(); }}
									title={des.name}
									style={des.type == 'texture' ? { backgroundImage: `url(/data/product-designs/${des.texture})` } : des.type == 'solid' ? { background: rgbToHex(des.color!) } : {}}
									href={`${info_url}/${getGetSingleProductTemplateUrl(des)}`}
								>
									{maybe(des.color_lock, cl => objectKeys(cl).map(color => <span key={color} style={{ backgroundColor: maybe(cl[color], rgbToHex) }} />))}
								</a>)}
							</div>
						</>}
						{!templatesAreColors && <>
							<p className="single-product__hashtag">{productTemplates.length} design templates available</p>
							<div className="single-product__template-list">
								{!!productTemplates.length && <ProductSlider
									product={product}
									className="product-page__products-related"
									productTemplates={productTemplates}
									size="small"
									handleClick={(pt, e) => { this.setProductTemplate(pt); e.preventDefault(); }}
									link={pt => `${info_url}/${getGetSingleProductTemplateUrl(pt)}`}
								/>}
							</div>
						</>}
						{pricing && <div className="single-product__pricing">
							<h3 className="single-product__pricing-title">Pricing {maybe(decoration && decTypeDescr[decoration], s => ` (${s})`)}</h3>
							<div className="single-product__pricing-table">
								<table>
									<thead>
										<tr>
											<th>Quantity:</th>
											{pricing.quantities.map(q => <th>{q}</th>)}
										</tr>
									</thead>
									<tbody>
										{pricing.rows.map(r => <tr>
											<th>{r.label}</th>
											{r.values.map(v => <td>{maybe(v, formatPrice) ?? '–'}</td>)}
										</tr>)}
									</tbody>
								</table>
							</div>
							{decoration == 'embroidery' && <p>Price for {pricing.stitchCount} stitches and up to 6 thread colors shown. To get pricing for a specific logo please order the <a href="/builder?product=digitize-for-embroidery">digitization service</a> beforehand.</p>}
							<p>
								{decoration == 'screen-print' && 'Other color combinations are possible. '}
								The actual discount increases with each additional unit purchased. For an exact quote, you can add any design to your cart, change the quantities, and receive a real time discount.
							</p>
							{features?.nameNumber && !isIn(decoration, ['embroidery', 'screen-print']) && <p>
								{'Personalized '}
								{decoration == 'cut-and-sew' && features.flag ? ' names, numbers, aliases, and country flags ' : ' names, numbers, and aliases '}
								{'  are included in your price for this production method.'}
							</p>}
							{decoration == 'embroidery'
								? <p>One-time {formatPrice(pricing.logoFee!)} fee for logo processing not included. Extra fees may apply for additional thread colors or oversized products.</p>
								: <p>Extra fees may apply for low-resolution logo processing or oversized products.</p>
							}
							{notEmpty(decAlternatives) && <p>
								This product is also available with{' '}
								{decAlternatives.map((it, i, arr) => [
									!i ? '' : i < arr.length - 1 ? ', ' : ' and ',
									<a href={`/product/${it.alias}`}>{decTypeDescr[it.decoration]}</a>,
								])}
								{' '}decoration.
							</p>}
						</div>}
						<div className="single-product__size">
							<span className="single-product__size__title">Sizes Available:</span>
							<div className="single-product__sizes__options">{groupedSizes.map(
								({ name, sizes }, idx) => <div className="single-product__sizes__options__item">
									{name}{sizes.length ? ': ' : ''}
									{sizes.join(', ')}
								</div>
							)}
							</div>
							{!!size_chart && <div className="single-product__links">
								{/*<a*/}
								{/*	href="#"*/}
								{/*	className="single-product__links__item single-product__links__item--style"*/}
								{/*>View Companion Styles</a>*/}
								{/*<span className="single-product__sizes__links__bar"></span>*/}
								<a href="#"
									className="single-product__links__item single-product__links__item--style"
									onClick={() => this.setState({ dialog: 'sizeCharts' })}
								>View Size Chart</a>
							</div>}
						</div>
					</>}
					{/*second slide section*/}



				</div>
			</section>

			{overview && <section className="single-product-overview">
				<h2 className="single-product-overview__title">Product Overview</h2>
				<ContentContext.Provider value={{
					target: ['web'],
					domain,
					website: `https://${domain.host}`,
					extras,
					link: s => s,
					image: s => s,
				}}>
					<ContentBlock item={overview} />
				</ContentContext.Provider>
			</section>}

			{information && <section className="single-product__information">
				<h2 className="single-product__information__title">Product Information</h2>
				<ContentContext.Provider value={{
					target: ['web'],
					domain,
					website: `https://${domain.host}`,
					extras,
					link: s => s,
					image: s => s,
				}}>
					<ContentBlock item={information} />
				</ContentContext.Provider>
			</section>}

			{isDpkg && <>
				<div className="single-product__attachment">

					<h2>
						<Icon name="checkbox" checked={notEmpty(uploads)} />
						Upload your {features?.maxOneLogo ? 'logo' : 'logos'}
					</h2>
					<input
						className="single-product__attachment-upload"
						id="upload"
						type="file"
						multiple={true}
						disabled={ssr}
						onChange={e => this.uploadLogo(e.target as HTMLInputElement)}
						onClick={e => (e.target as HTMLInputElement).value = ''}
					/>

					<div className="single-product__attachment-wrapper" onClick={() => this.openDialogBox()}>
						<Icon name="file-upload"/>
						<p >{id == 'vector'
							? 'Upload an image in PNG (recommended) or JPEG format.'
							: 'Upload an image in AI, SVG, EPS, PSD, PNG or JPEG format.'}</p>
					</div>
					<div className="single-product__attachment-preview">
						{uploads.length ? uploads.map(logoSingle => <div class="single-product__logo-wrapper">
							<button onClick={() => this.deleteLogo(logoSingle.id)}><Icon name="close" /> remove</button>
							<img className="single-product__logo" src={getUploadPreviewURL(logoSingle)} alt="logo"/>
						</div>) : <p className="single-product__attachment-info">Uploaded Logos Will Show Here.</p>}
					</div>
					{maybe(ownUploads, ups => <>
						<h3>Or pick an existing logo:</h3>
						<div className="single-product__attachment-preview">
							{ups.map(upload => <div class="single-product__logo-wrapper">
								<button onClick={() => this.selectUpload(upload)}>
									<Icon name="plus" />
									select
								</button>
								<img onClick={() => this.selectUpload(upload)} className="single-product__logo" src={getUploadPreviewURL(upload)} alt="logo"/>
							</div>)}
						</div>
					</>)}
				</div>
				{!!productsIncluded && (!selectKits ? <div className="single-product__products">
				
					<h2>
						<Icon name="checkbox" checked={!productsIncluded || !!productsSelected} />
						Choose the products
					</h2>
					
					{getProductsText()}
					
					<div className="single-product__items">
						{allowedKits?.map(kit => {
							const available = kit.products.map(indexInto(productMap)).filter(isSome);
							const selected = available.every(p => selectedProducts[p.id]);
							return <>
								<label
									className={classNames(
										'single-product__item',
										'--group',
										selected && '--selected'
									)}
								>
									<input
										type="checkbox"
										disabled={ssr}
										checked={selected}
										onChange={e => this.toggleSelection('products', kit.products, getEventChecked(e))}
									/>
									<span className="single-product__item-info">{kit.name}</span>
								</label>
								{available.map(productCheckbox)}
							</>;
						}) ?? products.map(productCheckbox)}
					</div>
				
				</div> : <div className="single-product__products">
					
					<h2>
						<Icon name="checkbox" checked={!!kitsSelected} />
						Choose the product kits
					</h2>
					
					{getProductsText()}
					
					<div className="single-product__items">
						{allowedKits.map(kit => {
							const available = kit.products.map(indexInto(productMap)).filter(isSome);
							return <>
								<label
									className={classNames(
										'single-product__item',
										'--wide',
										!!selectedKits[kit.id] && '--selected',
									)}
								>
									<input
										type="checkbox"
										disabled={ssr}
										checked={!!selectedKits[kit.id]}
										onChange={e => this.toggleSelection('kits', [kit.id], getEventChecked(e))}
									/>
									<span className="single-product__item-info">{kit.name}</span>
									<div class="single-product__item-image-set">
										{available
											.map(p => tuple(
												maybe(p.preview_image, getProductPreviewImageUrl),
												maybe(p.image_size, is => is[0] / is[1]),
											))
											.filter(i => isSome(i[0]) && isSome(i[1]))
											.sort((a, b) => b[1]! - a[1]!)
											.map(i => <Picture src={i[0]!} aria-hidden={true} loading="lazy" />)
										}
									</div>
								</label>
							</>;
						})}
					</div>
				
				</div>)}
				
				{colorList && apply(ensure(dpkgFeatures?.designColors), cc => <div className="single-product__package-colors">
					<h2>
						<Icon name="checkbox" checked={anyColorsSelected} />
						Select the design {cc > 1 ? 'colors' : 'color'}
					</h2>
					<p>
						Pick {cc > 1 ? ` up to ${cc} colors ` : ` the color `} you would like us to use for all-over-print items and other custom designs.
					</p>
					<div class="single-product__package-colors-pickers">
						{!ssr && <Suspense fallback={<Loading />}>
							{colorList.map((val, i) => <ColorPickerImpl
								key={i}
								value={val ?? [0, 0, 0]}
								profile="rgb"
								onChange={val => this.setState(
									s => ({ colorList: edit(s.colorList, [i], val) }),
								)}
							/>)}
						</Suspense>}
					</div>
				</div>)}
				
				{stockColorList && apply(ensure(dpkgFeatures?.stockColors), cc => <div className="single-product__package-colors">
					<h2>
						<Icon name="checkbox" checked={stockColorList.some(isSome)} />
						Select the stock product {cc > 1 ? 'colors' : 'color'}
					</h2>
					<p>
						Pick {cc > 1 ? ` up to ${cc} colors ` : ` the color `} you prefer for stock apparel. We will choose the closest available color option for each product.
					</p>
					<div class="single-product__package-colors-pickers">
						{!ssr && <Suspense fallback={<Loading />}>
							{stockColorList.map((val, i) => <ColorPickerImpl
								key={i}
								value={val ?? [0, 0, 0]}
								profile="rgb"
								onChange={val => this.setState(
									s => ({ stockColorList: edit(s.stockColorList, [i], val) }),
								)}
							/>)}
						</Suspense>}
					</div>
				</div>)}
				
				{templateSelection && <div className="single-product__products">
					
					<h2>
						<Icon name="checkbox" checked={!!templatesSelected} />
						Choose the {multiTemplates ? 'templates' : 'template'}
					</h2>
					
					<p class="single-product__products-text">
						Select {' '}{
							multiTemplates
								? `up to ${templateSelection.max} templates`
								: 'the template'
						}{' '}you'd like to feature on your sublimation product line.
					</p>
					
					<div className="single-product__items">
						{productTemplates.map(pt => <label
							className={classNames(
								'single-product__item',
								selectedTemplates[pt.template] && '--selected',
								(multiTemplates && maxTemplatesSelected && !selectedTemplates[pt.template]) && '--disabled',
							)}
						>
							{multiTemplates ? <input
								type="checkbox"
								checked={selectedTemplates[pt.template]}
								disabled={(maxTemplatesSelected && !selectedTemplates[pt.template]) || ssr}
								onChange={e => this.toggleSelection('templates', [pt.template], getEventChecked(e))}
							/> : <input
								type="radio"
								name="template"
								disabled={ssr}
								checked={selectedTemplates[pt.template]}
								onChange={e => {
									if (getEventChecked(e)) this.setState({ selectedTemplates: {
										[pt.template]: true,
									} });
								}}
							/>}
							<span className="single-product__item-info">
								{pt.name ?? pt.template}
							</span>
							<Picture
								src={makeDesignPreviewUrl(null, {
									product: templateSelection.product,
									productTemplate: pt.id,
									fullWidth: 512,
									side: 'front',
									colors: templateColors,
								})}
								alt={pt.name ?? pt.template}
								loading="lazy"
							/>
							{maybe(pt.demo_store_url, url => <a
								href={`/store/${url}`}
								class="single-product__item-demo-link"
								target="_blank" rel="noreferrer"
							>
								<span>Demo</span>
								<Icon name="pop-out" />
							</a>)}
						</label>)}
					</div>
				
				</div>}
				
				<div className="single-product__specification">
					<h2>
						<Icon name="checkbox" checked={!!comment?.trim().length} />
						Enter comments
					</h2>
					
					<div className="single-product__comment">
						<Input
							textarea={true}
							value={comment}
							handler={comment => this.setState({ comment })}
							attributes={{ placeholder: 'Enter any specific requests for our designers.' }}
						/>
					</div>
				</div>
				<button className="single-product__add-to-cart" type="button" onClick={() => this.addToCart()}>
					Add to cart ({formatPrice(product.price)})
				</button>
			</>}
			
			{reviews.length > 0 && <section className="single-product__reviews" id="reviews">
				<h2>Customer reviews</h2>
				<div className="single-product__reviews-inner">
					{reviews.map(r => <div className="review">
						<div className="review__ratings">
							<div className={classNames('review__rating', `--overall`)}>
								<StarBar rating={r.average} />
							</div>
							<div class="review__ratings-breakdown">
								{criteria.map(([key, label]) => <div className={classNames('review__rating', `--${key}`)}>
									<StarBar rating={r[key as keyof typeof orderRatingCriteria]} />
									<span>{label}</span>
								</div>)}
							</div>
						</div>
						<h4 className="review__info">
							{formatTimestamp(r.created)} by {r.public_name || <em>(anonymous)</em>}
						</h4>
						{!!r.comment?.trim() && <div className="review__text">{r.comment.trim()}</div>}
						{!!r.response?.trim() && <div className="review__response"><strong>{brand_name}:</strong>{' '}{r.response.trim()}</div>}
						{maybe(r.photos, all => {
							if (!all.length) return null;
							const rows = Math.min(3, Math.max(1, Math.round(all.length / 4)));
							const perRow = Math.min(6, Math.ceil(all.length / rows));
							return range(rows).map(rnum => {
								const row = all.slice(rnum * perRow, rnum * perRow + perRow);
								const total = row.reduce((a, p) => a + (p.width! / p.height!), 0);
								return <div className="review__photos">
									{row.map((p, i) => {
										const ar = p.width! / p.height!;
										return <a
											href={p.path}
											style={{ width: `${decimal(ar / total * 100)}%` }}
											onClick={e => this.showReviewPhoto(e, r, p)}
										>
											<Picture
												alt={`Review photo #${rnum * perRow + i + 1}`}
												src={`${p.path.replace(/\.[a-zA-Z]+$/, '.thumb.jpeg')}`}
												style={{ aspectRatio: decimal(ar) }}
											/>
										</a>;
									})}
								</div>;
							});
						})}
					</div>)}
				</div>
			</section>}
			
			<section className="single-product__footer__wrapper">
				<footer className="single-product__footer">
					
					<h3 className="single-product__footer__title">
						Product specialists available 5 days a week.
					</h3>
					<p className="single-product__footer__description">
						Unsure what to buy? We're here to help!
					</p>
					<p className="single-product__footer__support">
						<a href={`tel:${domain.phone_number}`}>{domain.phone_number}</a><br />
						<a href={`mailto:${domain.sales_email}`}>{domain.sales_email}</a>
						{/*<button className="single-product__footer__chat-btn">Chat Now</button>*/}
					</p>
				</footer>
			</section>
			
			{dialog == 'sizeCharts' && <Popup close={() => this.setState({ dialog: undefined })}>
				<SizeChartTable product={product} />
			</Popup>}
			
			{maybe(photoPopup, ({ review, photo }) => <Popup close={() => this.setState({ photoPopup: null })}>
				<div class="single-product__photo-popup" onClick={this.switchReviewPhoto}>
					<Picture
						src={photo.path}
						alt={maybe(review?.photos?.indexOf(photo), idx => idx >= 0 ? `Review photo ${idx + 1}` : null) ?? 'Product photo'}
					/>
				</div>
			</Popup>)}
		
		</main>;
	}
	
	@submitQueue
	async uploadLogo(el: HTMLInputElement) {
		const files = el.files;
		if (!files) return;
		for (let i = 0; i < files.length; i++) {
			const file = files.item(i);
			if (!file) continue;
			const res = await uploadArtwork({ file });
			this.selectUpload(res.upload);
		}
		
	}
	
	maybeCalcUploadColors = queue(async (upload: Upload) => {
		if (!this.state.colorList!.some(isNone)) return;
		const { colors } = await calculateUploadColors({ upload: upload.id });
		if (isEmpty(colors)) return;
		this.setState(s => ({
			colorList: s.colorList!.map(cur => cur ?? colors.shift()),
		}));
	});
	
	selectUpload(upload: Upload) {
		const features = ensure(this.props.product.features);
		const multi = !features.maxOneLogo;
		this.setState(state => ({
			uploads: multi ? [...state.uploads, upload] : [upload],
		}));
		if (features.designPackage?.designColors) {
			this.maybeCalcUploadColors(upload).catch(logRejection);
		}
	}

	deleteLogo(id:string) {
		const filterAttachment = this.state.uploads.filter(logos => logos.id !== id);
		this.setState({ uploads: filterAttachment });
	}

	openDialogBox() {
		const page = document.querySelector('.single-product__attachment-upload') as HTMLElement;
		page.click();
	}

	toggleSelection(what: 'products' | 'templates' | 'kits', ids: string[], on: boolean) {
		const key = `selected${capitalize(what)}` as const;
		this.setState(s => ({
			[key]: {
				...s[key],
				...Object.fromEntries(ids.map(id => [id, on])),
			},
		}));
	}
	
	@submitQueue
	async addToCart() {
		
		const { product } = this.props;
		const { colorList, stockColorList, comment, uploads, selectedTemplates, selectedProducts, selectedKits } = this.state;

		if (!uploads.length && !comment?.trim()) return await showAlert('Please upload a logo or enter a comment', 'warning');
		
		const products = trueKeys(selectedProducts);
		const kits = trueKeys(selectedKits);
		const templates = trueKeys(selectedTemplates);
		
		if (product.features?.designPackage?.templateSelection?.required && isEmptyOrNone(templates)) {
			return await showAlert('Please select a template', 'warning');
		}

		if (isEmptyOrNone(products) && isEmptyOrNone(kits)) {
			return await showAlert('Please select at least one product', 'warning');
		}

		const res = await addDesignPackageToCart({
			product: this.props.product.id,
			uploads: uploads.map(u => u.id),
			comment,
			products,
			kits,
			templates,
			colorList,
			stockColorList,
		});

		if (res.id) location.href = '/cart';

	}

	setProductTemplate(pt: ProductTemplate) {
		const curImg = this.state.selectedImage;
		const { context, product, images } = this.props;
		const selectedImage = curImg?.template_preview || curImg?.sanmar_preview ? curImg : images.find(pi => pi.template_preview) ?? curImg;
		this.setState({
			selectedProductTemplate: pt,
			selectedImage,
		});
		history.replaceState(null, '', `${product.info_url}/${getGetSingleProductTemplateUrl(pt)}`);
		document.title = `${product.name} – ${pt.name} – ${context.domain.brand_name}`;
	}
	
	showReviewPhoto(e: Event, review: OrderItemFeedbackEntry, photo: OrderItemPhoto) {
		e.preventDefault();
		e.stopPropagation();
		this.setState({
			photoPopup: { review, photo },
		});
	}
	
	switchReviewPhoto = (e: MouseEvent) => {
		const dir = e.clientX >= window.innerWidth / 2 ? 1 : -1;
		const { review, photo } = ensure(this.state.photoPopup);
		const all = review!.photos!;
		const cur = all.indexOf(photo);
		if (cur < 0 || all.length <= 1) return this.setState({ photoPopup: null });
		const idx = (all.length + cur + dir) % all.length;
		this.setState({ photoPopup: {
			review,
			photo: ensure(all[idx]),
		} });
	};
}
